/**
 * The Graph (GRT) Network Types
 *
 * Types for The Graph protocol monitoring.
 * Data sourced from the Graph Network Subgraph on Arbitrum.
 */

// ============================================
// CORE ENTITIES
// ============================================

/**
 * Delegator - User who delegates GRT to indexers
 */
export interface GraphDelegator {
  id: string; // Ethereum address (lowercase)
  totalStakedTokens: string; // BigInt as string
  totalUnstakedTokens: string;
  totalRealizedRewards: string;
  stakesCount: number;
  createdAt: number; // Block timestamp
  lastDelegatedAt?: number | null; // May not exist in new schema
  stakes: GraphDelegatedStake[];
}

/**
 * Delegated Stake - Relationship between delegator and indexer
 */
export interface GraphDelegatedStake {
  id: string; // delegator-indexer
  indexer: GraphIndexer;
  delegator?: { id: string };
  stakedTokens: string; // Current staked amount
  unstakedTokens: string; // Amount being unstaked
  lockedTokens: string; // Locked tokens (thawing)
  lockedUntil: number; // Block when tokens unlock
  realizedRewards: string; // Rewards claimed
  createdAt: number;
  lastDelegatedAt?: number | null; // May not exist in new schema
  lastUndelegatedAt?: number | null; // May not exist in new schema
  shareAmount: string; // Delegation pool shares
  personalExchangeRate: string; // Personal exchange rate
}

/**
 * Indexer - Node operator who indexes subgraphs
 */
export interface GraphIndexer {
  id: string; // Ethereum address
  createdAt: number;
  stakedTokens: string; // Self-stake
  allocatedTokens: string; // Currently allocated
  unstakedTokens: string;
  lockedTokens: string;
  tokensLockedUntil: number;
  delegatedTokens: string; // Tokens delegated to this indexer
  delegatorShares: string; // Total delegation pool shares
  delegationExchangeRate: string; // Current exchange rate
  indexingRewardCut: number; // % kept by indexer (0-1000000)
  queryFeeCut: number; // % of query fees kept (0-1000000)
  delegatorParameterCooldown: number;
  lastDelegationParameterUpdate: number;
  forcedClosures?: number;
  totalQueryFeesCollected?: string; // Not available for Indexer in current schema
  totalIndexingRewards?: string; // Not available for Indexer in current schema
  totalDelegatorQueryFeesCollected?: string;
  totalDelegatorIndexingRewards?: string;
  queryFeesCollected?: string;
  queryFeeRebates?: string;
  delegatorQueryFees?: string;
  delegatorIndexingRewards?: string;
  indexerIndexingRewards?: string;
  url: string | null;
  geoHash: string | null;
  defaultDisplayName: string | null;
  allocationsCount?: number; // Not available in current schema
  delegatorsCount?: number; // Not available in current schema
}

/**
 * Allocation - Indexer stake on a subgraph
 */
export interface GraphAllocation {
  id: string;
  indexer: { id: string };
  creator: string;
  activeForIndexer: { id: string } | null;
  subgraphDeployment: {
    id: string;
    originalName: string | null;
  };
  allocatedTokens: string;
  effectiveAllocation: string;
  createdAtEpoch: number;
  createdAtBlockHash: string;
  closedAtEpoch: number | null;
  closedAtBlockHash: string | null;
  queryFeesCollected: string;
  queryFeeRebates: string;
  curatorRewards: string;
  indexingRewards: string;
  indexingIndexerRewards: string;
  indexingDelegatorRewards: string;
  delegationFees: string;
  status: 'Active' | 'Closed' | 'Finalized' | 'Claimed';
}

/**
 * Graph Network - Global protocol stats
 */
export interface GraphNetworkStats {
  id: string;
  // Token metrics
  totalSupply: string;
  totalTokensStaked: string;
  totalDelegatedTokens: string;
  totalTokensAllocated: string;
  totalTokensSignalled: string;
  totalTokensSignalledAutoMigrate: string;
  totalTokensSignalledDirectly: string;

  // Rewards
  totalIndexingRewards: string;
  totalQueryFees: string;
  totalCuratorQueryFees: string;
  totalDelegatorQueryFeeRebates: string;

  // Network participants (some fields may not exist in new schema)
  totalIndexerCount?: number;
  activeIndexerCount: number;
  totalDelegatorCount?: number;
  activeDelegatorCount: number;
  totalCuratorCount?: number;
  activeCuratorCount: number;
  totalSubgraphCount?: number;
  activeSubgraphCount: number;

  // Epochs
  currentEpoch: number;
  epochLength: number; // Blocks per epoch
  lastRunEpoch: number;
  lastLengthUpdateEpoch: number;
  lastLengthUpdateBlock: number;

  // Protocol parameters
  networkGRTIssuancePerBlock: string;
  epochManagerImplementations: string[];

  // Curation
  curationPercentage: number;
  protocolFeePercentage: number;
  delegationRatio: number;
  delegationParametersCooldown: number;
  minimumIndexerStake: string;

  // Slashing
  slashingPercentage: number;
  thawingPeriod: number; // Blocks

  // Delegation
  delegationTaxPercentage: number; // 0.5% = 5000

  // Arbitrum
  arbitrator: string;

  // Rewards
  totalGRTMinted: string;
  totalGRTBurned: string;
  totalGRTWithdrawn: string;
  totalGRTDeposited: string;
  totalGRTWithdrawnConfirmed: string;
  totalGRTMintedFromL2: string;
}

/**
 * Epoch - Network statistics per epoch
 */
export interface GraphEpoch {
  id: string;
  startBlock: number;
  endBlock: number;
  signalledTokens: string;
  stakeDeposited: string;
  totalQueryFees: string;
  taxedQueryFees: string;
  queryFeesCollected: string;
  curatorQueryFees: string;
  queryFeeRebates: string;
  totalRewards: string;
  totalIndexerRewards: string;
  totalDelegatorRewards: string;
}

// ============================================
// WALLET DATA (Aggregated for display)
// ============================================

/**
 * Processed wallet data for display
 */
export interface TheGraphWalletData {
  address: string;

  // Balance (from external source if needed)
  grtBalance: number;
  grtBalanceUsd: number;

  // Delegation stats
  totalDelegated: number; // Total GRT delegated
  totalDelegatedUsd: number;
  totalUnstaking: number; // Being undelegated
  totalLocked: number; // Locked (thawing)

  // Rewards
  totalRealizedRewards: number;
  totalRealizedRewardsUsd: number;
  pendingRewards: number; // Estimated pending
  pendingRewardsUsd: number;

  // Delegations
  delegationsCount: number;
  delegations: TheGraphDelegation[];

  // Metadata
  firstDelegatedAt: Date | null;
  lastDelegatedAt: Date | null;
}

/**
 * Processed delegation for display
 */
export interface TheGraphDelegation {
  id: string;
  indexer: {
    id: string;
    name: string | null;
    url: string | null;
    indexingRewardCut: number; // Percentage (0-100)
    queryFeeCut: number; // Percentage (0-100)
    totalDelegated: number;
    ownStake: number;
    delegatorCount?: number; // Not available in current schema
    allocationsCount?: number; // Not available in current schema
  };
  stakedTokens: number;
  stakedTokensUsd: number;
  unstakedTokens: number;
  lockedTokens: number;
  lockedUntil: Date | null;
  realizedRewards: number;
  realizedRewardsUsd: number;
  shareAmount: number;
  personalExchangeRate: number;
  currentExchangeRate: number;
  estimatedPendingRewards: number;
  estimatedApy: number; // Estimated APY based on indexer performance
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface GraphNetworkSubgraphResponse {
  data: {
    delegator?: GraphDelegator;
    delegators?: GraphDelegator[];
    graphNetwork?: GraphNetworkStats;
    graphNetworks?: GraphNetworkStats[];
    indexer?: GraphIndexer;
    indexers?: GraphIndexer[];
    epochs?: GraphEpoch[];
    delegatedStakes?: GraphDelegatedStake[];
  };
  errors?: Array<{ message: string }>;
}

// ============================================
// CONSTANTS
// ============================================

export const THEGRAPH_CONSTANTS = {
  // Network
  DELEGATION_TAX_PERCENTAGE: 0.5, // 0.5% tax on delegation
  THAWING_PERIOD_DAYS: 28, // ~28 days to undelegate
  EPOCH_LENGTH_HOURS: 24, // ~24 hours per epoch
  DELEGATION_RATIO: 16, // Max 16x delegated vs self-stake

  // Rewards
  ANNUAL_INFLATION_RATE: 0.03, // 3% annual inflation
  INDEXER_REWARDS_SHARE: 0.9, // 90% of inflation to indexers

  // Minimums
  MINIMUM_INDEXER_STAKE: 100_000, // 100K GRT minimum

  // Token decimals
  GRT_DECIMALS: 18,
};

// ============================================
// COLORS (for UI)
// ============================================

export const THEGRAPH_COLORS = {
  primary: '#6747ED', // The Graph purple
  secondary: '#4B3BA3', // Darker purple
  accent: '#9D8CFF', // Light purple
  gradient: 'linear-gradient(135deg, #6747ED, #4B3BA3)',
  lightGradient: 'linear-gradient(135deg, rgba(103, 71, 237, 0.15), rgba(75, 59, 163, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
};
